import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Button, Tooltip } from "@mui/material";
import { $getNodeByKey } from "lexical";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSupabaseLoader } from "../../../../../../server/supabase/hooks";
import { getAspectRatio } from "../../../../../../utils/aspect-ratio";
import type { CreateAssetResult } from "../../../../../assets/file-upload";
import type { Asset } from "../../../../../assets/server/digital-asset-service";
import { ConditionalWrapper } from "../../../../../generic/components/conditional-wrapper";
import { useClaims } from "../../../../../generic/hooks/use-claims";
import { SupabaseSlackAppService } from "../../../../../slack/server/slack-supabase-service";
import { useLexicalContext } from "../../../lexical-context";
import { $createDesignHuddleVideoNodeFromAsset } from "../../../nodes/design-huddle-mui";
import { DefaultProportionOfMaxWidth } from "../../../nodes/design-huddle-node";
import { INSERT_VIDEO_COMMAND } from "../../../types/commands";
import { $insertNode } from "../../../utils/insert-node";
import { insertUrl } from "../../image-plugin";

export const InsertVideoButton: FC<{
  asset: Pick<
    Asset,
    "content_type" | "public_url" | "mime_type" | "project_id" | "thumbnail_url" | "id" | "title"
  > | null;
  onClose: () => void;
  onInsert?: (url: string) => Promise<CreateAssetResult>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  nodeKey?: string;
}> = ({ asset, loading, nodeKey, setLoading, onClose }) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const user = useClaims();
  const { channelName: channel } = useLexicalContext();

  const slackAppLoader = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (channel !== "slack") return { videoSupported: false };
      return {
        videoSupported: await new SupabaseSlackAppService(supabase).videoEnabled(account_id),
      };
    },
    [channel],
  );

  const handleInsertAsset = useCallback(async () => {
    setLoading(true);
    if (!asset || !user) return;

    if (
      asset.content_type === "Uploaded File" &&
      asset.mime_type?.startsWith("video/") &&
      asset.public_url &&
      asset.thumbnail_url
    ) {
      onClose();
      const file = await insertUrl(asset.thumbnail_url, channel);

      editor.dispatchCommand(INSERT_VIDEO_COMMAND, {
        videoUrl: asset.public_url,
        thumbnailUrl: asset.thumbnail_url,
        aspect: file.aspect,
        proportionOfMaxWidth: file.proportionOfMaxWidth,
        sourceAssetId: asset.id,
        title: channel === "slack" ? asset.title : undefined,
      });
      setLoading(false);
      onClose();
      return;
    }

    const aspect = asset.thumbnail_url ? await getAspectRatio(asset.thumbnail_url) : undefined;

    editor.update(
      () => {
        if (asset.public_url && asset.thumbnail_url && asset.project_id) {
          const node = $createDesignHuddleVideoNodeFromAsset(
            editor._headless,
            user["http://changeengine.com/account_id"],
            asset.project_id,
            asset.thumbnail_url,
            asset.public_url,
            DefaultProportionOfMaxWidth,
            aspect,
            asset.id,
          );

          if (nodeKey) {
            const oldNode = $getNodeByKey(nodeKey);
            oldNode && oldNode.replace(node);
          } else {
            $insertNode(node);
          }
        }
      },
      { discrete: true },
    );

    setLoading(false);
    onClose();
    return;
  }, [editor, asset, user, channel, nodeKey, onClose, setLoading]);

  return (
    (asset?.mime_type?.startsWith("video/") || asset?.content_type === "Video") && (
      <ConditionalWrapper
        condition={
          channel === "slack" && !slackAppLoader.loading && !slackAppLoader.data?.videoSupported
        }
        wrapper={(children) => (
          <Tooltip title="Update your Slack app to enable embedded videos">
            <span>{children}</span>
          </Tooltip>
        )}
      >
        <Button
          data-analytics-id="editor-content-library-insert-video"
          data-testid="insert-upload-as-video-button"
          type="button"
          disabled={asset === null || (channel === "slack" && !slackAppLoader.data?.videoSupported)}
          loading={loading}
          onClick={() => {
            void handleInsertAsset();
          }}
        >
          {channel === "slack" ? t("Embed video") : t("Insert link as thumbnail")}
        </Button>
      </ConditionalWrapper>
    )
  );
};
